/** @format */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { debounce } from "lodash";
import { useCallback, useMemo } from "react";

import galaxieClient from "@/api/galaxieClient";
import { useUser } from "@/api/userAPI";
import { useNotificationsEnabled } from "@/hooks/useStableFeatureFlags";

export interface NotificationPreferences {
  id: string;
  pushEnabled: boolean;
  enabledCategories: string[];
  quietHoursEnabled: boolean;
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
  maxNotificationsPerHour: number;
  maxNotificationsPerDay: number;
  notifyFavoriteDriversOnly: boolean;
  positionChangeThreshold: number;
  scheduleReminderMinutes: number;
  toastDisplaySeconds: number;
  toastPersistent: boolean;
  updatedAt: string;
}

export function useNotificationPreferences() {
  const queryClient = useQueryClient();
  const { data: user } = useUser();
  const notificationsEnabled = useNotificationsEnabled();

  const { data, isLoading, error } = useQuery<NotificationPreferences>({
    queryKey: ["notification-preferences"],
    queryFn: async () => {
      const response = await galaxieClient.get("/notifications/preferences/");
      return response.data as NotificationPreferences;
    },
    enabled: !!user?.id && notificationsEnabled,
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<NotificationPreferences>) => {
      const response = await galaxieClient.patch(
        "/notifications/preferences/",
        updates
      );
      return response.data as NotificationPreferences;
    },
    onMutate: async updates => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["notification-preferences"],
      });

      // Snapshot current value for rollback
      const previous = queryClient.getQueryData<NotificationPreferences>([
        "notification-preferences",
      ]);

      // Optimistically update the cache
      if (previous) {
        queryClient.setQueryData<NotificationPreferences>(
          ["notification-preferences"],
          { ...previous, ...updates }
        );
      }

      return { previous };
    },
    onError: (_err, _updates, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(["notification-preferences"], context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["notification-preferences"],
      });
    },
  });

  // Debounced version for non-critical fields (categories, quiet hours, etc.)
  // to avoid firing a PATCH on every rapid checkbox toggle
  const debouncedMutate = useMemo(
    () =>
      debounce((updates: Partial<NotificationPreferences>) => {
        updateMutation.mutate(updates);
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateMutation.mutate]
  );

  // Stable debounced callback
  const updatePreferencesDebounced = useCallback(
    (updates: Partial<NotificationPreferences>) => {
      debouncedMutate(updates);
    },
    [debouncedMutate]
  );

  return {
    preferences: data,
    isLoading,
    error,
    updatePreferences: updatePreferencesDebounced,
    updatePreferencesImmediate: updateMutation.mutate,
    updatePreferencesAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}
